import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { inject, Injectable } from '@angular/core';
import { ServiceForRoute } from '@api/models/MonitoringService/Contracts/service-for-route';
import { RouteNew } from '@api/models/MonitoringService/Contracts/route-new';
import { MonitoringService } from '@api/services/monitoring.service';
import { VisOrgFiltered } from '@api/models/UserService/Contract/vis-org-filtered';
import { AbstractListModel } from '@libs/factory/abstract-list/abstract-list.model';
import { Observable, tap } from 'rxjs';
import { CustomRxState } from '@core/state/rx.state';
import { VisOrgService } from '@api/services/vis-org.service';
import { OrganizationFilterType } from '@core/organization/organization-types.model';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { ListStPointsResponse } from '@api/models/MonitoringService/Contracts/New/list-st-points-response';
import { FilterMonitoringModel } from '@core/monitoring/monitoring-filter.model';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { StorageKeys } from '@core/storage/storage.keys';
import { SessionStorage } from 'ngx-webstorage';


interface IMonitoringState {
    loading: Record<StartWithUnit, boolean>;
    services: ServiceForRoute[];
    routes: RouteNew[];
    providers: VisOrgFiltered[];
    shiftStatuses: AbstractListModel[];
    netStatuses: AbstractListModel[];
    validatorStatuses: AbstractListModel[];
    filter: FilterMonitoringModel;
}

interface IMonitoringActions {
    setFilter: FilterMonitoringModel;
}

@Injectable()
export class MonitoringState extends CustomRxState<IMonitoringState> {
    @SessionStorage(StorageKeys.MonitoringFilter)
    public storageFilter: FilterMonitoringModel;

    readonly #monitoringService: MonitoringService = inject(MonitoringService);
    readonly #visOrgService: VisOrgService = inject(VisOrgService);

    readonly #netNullStatus: string = marker('data.monitoringValidatorNetworkStatus.null');
    readonly #actions: RxActions<IMonitoringActions> = new RxActionFactory<IMonitoringActions>().create();

    public readonly services$: Observable<ServiceForRoute[]> = this.selectLazy('services', this.requestServices);
    public readonly routes$: Observable<RouteNew[]> = this.selectLazy('routes', this.requestRoutes);
    public readonly providers$: Observable<VisOrgFiltered[]> = this.selectLazy('providers', this.requestOrganizations);
    public readonly shiftStatuses$: Observable<AbstractListModel[]> = this.select('shiftStatuses');
    public readonly netStatuses$: Observable<AbstractListModel[]> = this.select('netStatuses');
    public readonly validatorStatuses$: Observable<AbstractListModel[]> = this.select('validatorStatuses');
    public readonly filter$: Observable<FilterMonitoringModel> = this.select('filter');

    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();
    }

    public setFilter(filter: FilterMonitoringModel): void {
        this.#actions.setFilter(filter);
    }

    public getStationsBySrvId(srvId: number | null): Observable<ListStPointsResponse[]> {
        return this.#monitoringService.apiMonitoringListStPointsGet({ ...(srvId ? { srvId } : null) });
    }

    private requestServices(): Observable<ServiceForRoute[]> {
        return this.#monitoringService.apiMonitoringListServicesGet();
    }

    private requestRoutes(): Observable<RouteNew[]> {
        return this.#monitoringService.apiMonitoringListRoutesGet();
    }

    private requestOrganizations(): Observable<VisOrgFiltered[]> {
        return this.#visOrgService.apiUsersListVisOrgPost({
            body: {
                orgTypes: [OrganizationFilterType.Provider, OrganizationFilterType.Platform],
                isDept: 0,
            },
        });
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            filter: this.storageFilter || null,
            services: null,
            routes: null,
            providers: null,
            validatorStatuses: [
                {
                    id: 1,
                    title: marker('data.monitoringValidatorStatus.1'),
                },
                {
                    id: 2,
                    title: marker('data.monitoringValidatorStatus.2'),
                },
                {
                    id: 4,
                    title: marker('data.monitoringValidatorStatus.4'),
                },
            ],
            netStatuses: [
                {
                    id: 0,
                    title: marker('data.monitoringValidatorNetworkStatus.0'),
                },
                {
                    id: 1,
                    title: marker('data.monitoringValidatorNetworkStatus.1'),
                },
                {
                    id: 2,
                    title: marker('data.monitoringValidatorNetworkStatus.2'),
                },
            ],
            shiftStatuses: [
                {
                    id: -1,
                    title: marker('data.monitoringValidatorShiftStatus.-1'),
                },
                {
                    id: 0,
                    title: marker('data.monitoringValidatorShiftStatus.0'),
                },
                {
                    id: 1,
                    title: marker('data.monitoringValidatorShiftStatus.1'),
                },
                {
                    id: 2,
                    title: marker('data.monitoringValidatorShiftStatus.2'),
                },
            ],
        });
    }

    private connectSelectors(): void {
        this.connect('filter', this.#actions.setFilter$.pipe(
            tap((filter: FilterMonitoringModel) => this.storageFilter = filter),
        ));
    }

}
